'use client';
import React, { useEffect } from 'react';
import Image from 'next/image';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const PHONE = '9492935721';
const PHONE_DISPLAY = '(949) 293-5721';
const SMS_LINK = `sms:${PHONE}`;
const CALL_LINK = `tel:+1${PHONE}`;

const process = [
  {
    number: '01',
    title: 'Assessment & Prep',
    desc: 'We inspect your vehicle thoroughly and build a service plan around its specific condition — not a one-size-fits-all checklist.',
  },
  {
    number: '02',
    title: 'Deep Cleaning',
    desc: 'A multi-stage wash process using professional-grade products and techniques designed to protect luxury paint, not just clean it.',
  },
  {
    number: '03',
    title: 'Protection & Enhancement',
    desc: 'Ceramic coatings, paint correction, and protective treatments applied with precision — to preserve your vehicle\'s finish and long-term value.',
  },
  {
    number: '04',
    title: 'Final Inspection',
    desc: 'A thorough quality check and final detailing pass to ensure every surface meets our standard before we consider the job done.',
  },
];

const stats = [
  { value: '500+', label: 'Luxury Vehicles' },
  { value: '100%', label: 'Client Satisfaction' },
  { value: '5+', label: 'Years Experience' },
  { value: '24/7', label: 'Concierge Support' },
];

const testimonials = [
  {
    quote: 'Exceptional service! They transformed my Porsche 911 to showroom condition. Highly professional and detail-oriented.',
    name: 'Michael Rodriguez',
    location: 'Newport Beach, CA',
    vehicle: 'Porsche 911',
    stars: 5,
  },
  {
    quote: 'DA1 MAMBA exceeded expectations. My Range Rover has never looked better. Worth every penny for the premium service.',
    name: 'Jennifer Chen',
    location: 'Irvine, CA',
    vehicle: 'Range Rover',
    stars: 5,
  },
  {
    quote: 'DA1 MAMBA is the only detailing service I trust with my collection. Quality and service are consistently exceptional.',
    name: 'David Kim',
    location: 'Orange County, CA',
    vehicle: 'Collector',
    stars: 5,
  },
];

export default function AboutPage() {
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal:not(.visible)').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 100);
            });
          }
        });
      },
      { threshold: 0.06 }
    );
    document.querySelectorAll('section')?.forEach((s) => obs?.observe(s));
    return () => obs?.disconnect();
  }, []);

  return (
    <div style={{ background: 'var(--mamba-black)', minHeight: '100vh' }}>
      <Header />
      <main>
        {/* Hero */}
        <section
          style={{
            paddingTop: '0',
            paddingBottom: '0',
            position: 'relative',
            overflow: 'hidden',
            minHeight: '60vh',
            display: 'flex',
            alignItems: 'flex-end',
          }}
        >
          {/* Background image */}
          <div className="absolute inset-0">
            <Image
              src="https://da1mambadetailing.com/assets/416006343_685055570326614_4483350650480443101_n_1751763806956.jpg"
              alt="Professional luxury vehicle detailing by DA1MAMBA"
              fill
              className="object-cover object-center"
              priority
              quality={90}
            />
          </div>
          {/* Overlays */}
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(100deg, rgba(5,5,5,0.95) 0%, rgba(5,5,5,0.75) 50%, rgba(5,5,5,0.3) 100%)' }}
          />
          <div
            className="absolute bottom-0 inset-x-0"
            style={{ height: '50%', background: 'linear-gradient(to top, rgba(10,10,10,1) 0%, transparent 100%)' }}
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse 50% 80% at 20% 50%, rgba(107,33,168,0.12) 0%, transparent 70%)' }}
          />
          <div className="max-w-7xl mx-auto px-6 relative z-10 pb-20 pt-40 w-full">
            <div className="label-gold mb-5 reveal">
              <span style={{ width: '40px', height: '1px', background: 'var(--mamba-gold-light)', display: 'inline-block' }} />
              About DA1MAMBA
            </div>
            <h1
              className="reveal reveal-delay-1"
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 900,
                fontSize: 'clamp(44px, 7vw, 88px)',
                lineHeight: '0.95',
                letterSpacing: '-0.03em',
                color: 'var(--white)',
                marginBottom: '20px',
              }}
            >
              Built on<br />
              <span
                style={{
                  background: 'linear-gradient(135deg, var(--mamba-purple-bright), #9333ea 50%, var(--mamba-gold-light))',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                obsession.
              </span>
            </h1>
            <p className="reveal reveal-delay-2" style={{ color: 'rgba(209,213,219,0.8)', fontSize: '18px', lineHeight: '1.65', fontFamily: 'DM Sans, sans-serif', maxWidth: '520px' }}>
              DA1MAMBA isn't just another detailing service — it's a standard. One we hold ourselves to on every vehicle, every time.
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section style={{ padding: '100px 0', background: 'var(--mamba-black)' }}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* Left — Image */}
              <div className="reveal">
                <div
                  style={{
                    position: 'relative',
                    aspectRatio: '4/5',
                    overflow: 'hidden',
                    borderRadius: '2px',
                    border: '1px solid rgba(107,33,168,0.2)',
                  }}
                >
                  <Image
                    src="https://da1mambadetailing.com/assets/416006343_685055570326614_4483350650480443101_n_1751763806956.jpg"
                    alt="Professional Mercedes-Benz interior detailing by DA1MAMBA"
                    fill
                    className="object-cover"
                    sizes="(max-width: 1024px) 100vw, 50vw"
                  />
                  {/* Gold corner accent */}
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '24px',
                      right: '24px',
                      background: 'rgba(10,10,10,0.85)',
                      border: '1px solid rgba(212,175,55,0.3)',
                      padding: '16px 20px',
                      backdropFilter: 'blur(8px)',
                    }}
                  >
                    <div style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: '22px', color: 'var(--white)', lineHeight: 1 }}>
                      5+
                    </div>
                    <div style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: 'var(--mamba-gold-light)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      Years of Excellence
                    </div>
                  </div>
                </div>
              </div>

              {/* Right — Story */}
              <div>
                <div className="label-gold mb-4 reveal">
                  <span style={{ width: '32px', height: '1px', background: 'var(--mamba-gold-light)', display: 'inline-block' }} />
                  Our Story
                </div>
                <h2
                  className="section-heading reveal reveal-delay-1"
                  style={{ fontSize: 'clamp(28px, 3.5vw, 44px)', marginBottom: '24px' }}
                >
                  Not just a detail.<br />
                  <span className="text-gradient-purple">A standard of care.</span>
                </h2>

                <div className="flex flex-col gap-5 reveal reveal-delay-2">
                  <p style={{ color: 'var(--cool-gray)', fontSize: '16px', lineHeight: '1.8', fontFamily: 'DM Sans, sans-serif' }}>
                    DA1MAMBA PROFESSIONAL DETAILING was built around one belief: your car deserves better. Better products, better technique, and a detailer who actually cares about the result — not just the clock.
                  </p>
                  <p style={{ color: 'var(--cool-gray)', fontSize: '16px', lineHeight: '1.8', fontFamily: 'DM Sans, sans-serif' }}>
                    We specialize in concierge-style mobile service, delivering dealership-level results at your home or office. No automated brushes, no recycled towels, no rushed jobs. Just a meticulous process built around protecting the vehicles our clients love.
                  </p>
                  <p style={{ color: 'var(--cool-gray)', fontSize: '16px', lineHeight: '1.8', fontFamily: 'DM Sans, sans-serif' }}>
                    From a weekly maintenance wash to a full multi-stage paint correction, every service is personalized to your vehicle's specific needs — and delivered with the same white-glove attention, every single time.
                  </p>
                </div>

                <div className="flex gap-3 mt-8 reveal reveal-delay-3">
                  <a href={SMS_LINK} className="btn-primary" style={{ fontSize: '13px', padding: '14px 28px' }}>
                    Text for a Quote
                  </a>
                  <a href={CALL_LINK} className="btn-gold" style={{ fontSize: '13px', padding: '13px 24px' }}>
                    Call {PHONE_DISPLAY}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="section-divider" />

        {/* Stats */}
        <section style={{ padding: '80px 0', background: 'var(--mamba-graphite)' }}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px" style={{ background: 'rgba(107,33,168,0.15)', border: '1px solid rgba(107,33,168,0.2)' }}>
              {stats?.map((s, i) => (
                <div
                  key={i}
                  className="reveal"
                  style={{
                    transitionDelay: `${i * 0.1}s`,
                    background: 'var(--mamba-graphite-mid)',
                    padding: '48px 32px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <div className="stat-number text-gradient-purple">{s?.value}</div>
                  <div style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: 'var(--gray-dim)', textAlign: 'center', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    {s?.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="section-divider" />

        {/* Our Process */}
        <section style={{ padding: '100px 0', background: 'var(--mamba-black)' }}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="label-gold mb-4 reveal">
              <span style={{ width: '32px', height: '1px', background: 'var(--mamba-gold-light)', display: 'inline-block' }} />
              Our Process
            </div>
            <h2
              className="section-heading reveal reveal-delay-1"
              style={{ fontSize: 'clamp(28px, 3.5vw, 44px)', marginBottom: '12px' }}
            >
              How we work —<br />
              <span className="text-gradient-purple">step by step.</span>
            </h2>
            <p className="reveal reveal-delay-2" style={{ color: 'var(--gray-dim)', fontSize: '15px', fontFamily: 'DM Sans, sans-serif', marginBottom: '56px', maxWidth: '480px' }}>
              A consistent, proven process that protects your vehicle and delivers results you can see.
            </p>

            {/* Process grid — asymmetric */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px" style={{ background: 'rgba(107,33,168,0.12)', border: '1px solid rgba(107,33,168,0.12)' }}>
              {process.map((step, i) => (
                <div
                  key={i}
                  className="reveal"
                  style={{
                    transitionDelay: `${i * 0.1}s`,
                    background: 'var(--mamba-graphite-mid)',
                    padding: '48px 40px',
                    display: 'flex',
                    gap: '24px',
                    alignItems: 'flex-start',
                  }}
                >
                  <div
                    style={{
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 900,
                      fontSize: '48px',
                      lineHeight: 1,
                      color: 'rgba(107,33,168,0.2)',
                      flexShrink: 0,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {step?.number}
                  </div>
                  <div>
                    <h3 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: '20px', color: 'var(--white)', marginBottom: '10px' }}>
                      {step?.title}
                    </h3>
                    <p style={{ color: 'var(--gray-dim)', fontSize: '15px', lineHeight: '1.7', fontFamily: 'DM Sans, sans-serif' }}>
                      {step?.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="section-divider" />

        {/* Testimonials */}
        <section style={{ padding: '100px 0', background: 'var(--mamba-graphite)' }}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="label-gold mb-4 reveal">
              <span style={{ width: '32px', height: '1px', background: 'var(--mamba-gold-light)', display: 'inline-block' }} />
              Client Testimonials
            </div>
            <h2 className="section-heading reveal reveal-delay-1" style={{ fontSize: 'clamp(28px, 3.5vw, 44px)', marginBottom: '48px' }}>
              What our clients say.
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials?.map((t, i) => (
                <div
                  key={i}
                  className="testimonial-card reveal"
                  style={{ transitionDelay: `${i * 0.12}s` }}
                >
                  <div className="flex gap-1 mb-5" style={{ position: 'relative', zIndex: 1 }}>
                    {Array.from({ length: t?.stars })?.map((_, si) => (
                      <svg key={si} width="14" height="14" fill="var(--mamba-gold-light)" viewBox="0 0 24 24">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                    ))}
                  </div>
                  <p style={{ color: 'var(--cool-gray)', fontSize: '15px', lineHeight: '1.7', fontFamily: 'DM Sans, sans-serif', marginBottom: '24px', position: 'relative', zIndex: 1 }}>
                    "{t?.quote}"
                  </p>
                  <div style={{ borderTop: '1px solid rgba(107,33,168,0.15)', paddingTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <div>
                      <div style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '14px', color: 'var(--white)' }}>{t?.name}</div>
                      <div style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: 'var(--gray-dim)' }}>{t?.location}</div>
                    </div>
                    <div style={{ background: 'rgba(107,33,168,0.12)', border: '1px solid rgba(107,33,168,0.25)', padding: '4px 10px', fontSize: '11px', fontFamily: 'Manrope, sans-serif', fontWeight: 600, color: 'var(--mamba-purple-bright)', letterSpacing: '0.04em' }}>
                      {t?.vehicle}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="section-divider" />

        {/* Contact / Get in Touch */}
        <section style={{ padding: '100px 0', background: 'var(--mamba-black)' }}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Left */}
              <div>
                <div className="label-gold mb-4 reveal">
                  <span style={{ width: '32px', height: '1px', background: 'var(--mamba-gold-light)', display: 'inline-block' }} />
                  Get in Touch
                </div>
                <h2 className="section-heading reveal reveal-delay-1" style={{ fontSize: 'clamp(28px, 3.5vw, 44px)', marginBottom: '16px' }}>
                  Ready to experience<br />
                  <span className="text-gradient-purple">the DA1MAMBA standard?</span>
                </h2>
                <p className="reveal reveal-delay-2" style={{ color: 'var(--gray-dim)', fontSize: '16px', lineHeight: '1.7', fontFamily: 'DM Sans, sans-serif', marginBottom: '40px' }}>
                  Text or call us to schedule your service. We'll confirm within 2 hours and come to you — fully equipped, ready to work.
                </p>
                <div className="flex flex-col gap-3 reveal reveal-delay-3">
                  <a href={SMS_LINK} className="btn-primary" style={{ fontSize: '14px', padding: '16px 32px', justifyContent: 'center' }}>
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                    </svg>
                    Text for a Quote
                  </a>
                  <a href={CALL_LINK} className="btn-gold" style={{ fontSize: '14px', padding: '15px 32px', justifyContent: 'center' }}>
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.01 1.18 2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z"/>
                    </svg>
                    Call {PHONE_DISPLAY}
                  </a>
                  <a
                    href="https://book.squareup.com/appointments/04o76ql3vbtxkz/location/LM68HVRDTDX7X/services"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-ghost"
                    style={{ fontSize: '14px', padding: '15px 32px', justifyContent: 'center' }}
                  >
                    Book Online (Square)
                  </a>
                </div>
              </div>

              {/* Right — Contact details */}
              <div className="reveal reveal-delay-2">
                <div
                  style={{
                    background: 'var(--mamba-graphite-mid)',
                    border: '1px solid rgba(107,33,168,0.2)',
                    padding: '40px',
                  }}
                >
                  <div className="label-gold mb-6" style={{ fontSize: '10px' }}>Contact Information</div>
                  <div className="flex flex-col gap-6">
                    {[
                      {
                        label: 'Phone / Text',
                        value: PHONE_DISPLAY,
                        href: CALL_LINK,
                        icon: (
                          <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                            <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.01 1.18 2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z"/>
                          </svg>
                        ),
                      },
                      {
                        label: 'Email',
                        value: 'info@da1mambadetailing.com',
                        href: 'mailto:info@da1mambadetailing.com',
                        icon: (
                          <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                          </svg>
                        ),
                      },
                      {
                        label: 'Service Area',
                        value: 'Newport Beach, Irvine & Orange County',
                        href: null,
                        icon: (
                          <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                          </svg>
                        ),
                      },
                    ]?.map((item) => (
                      <div key={item?.label} className="flex items-start gap-4">
                        <div style={{ color: 'var(--mamba-gold-light)', flexShrink: 0, marginTop: '2px' }}>
                          {item?.icon}
                        </div>
                        <div>
                          <div style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: '11px', color: 'var(--gray-dim)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '4px' }}>
                            {item?.label}
                          </div>
                          {item?.href ? (
                            <a
                              href={item?.href}
                              style={{ color: 'var(--white)', fontSize: '15px', fontFamily: 'DM Sans, sans-serif', textDecoration: 'none', transition: 'color 0.2s' }}
                              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--mamba-gold-light)')}
                              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--white)')}
                            >
                              {item?.value}
                            </a>
                          ) : (
                            <span style={{ color: 'var(--white)', fontSize: '15px', fontFamily: 'DM Sans, sans-serif' }}>
                              {item?.value}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Social */}
                  <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid rgba(107,33,168,0.15)' }}>
                    <div className="label-gold mb-4" style={{ fontSize: '10px' }}>Follow Us</div>
                    <div className="flex gap-4">
                      {[
                        { href: 'https://www.instagram.com/da1mamba/', label: 'Instagram @da1mamba', icon: (
                          <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                            <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="0.5" fill="currentColor"/>
                          </svg>
                        )},
                        { href: 'https://www.tiktok.com/@da1mamba', label: 'TikTok @da1mamba', icon: (
                          <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.84 4.84 0 0 1-1.01-.07z"/>
                          </svg>
                        )},
                        { href: 'https://www.youtube.com/@da1mamba', label: 'YouTube @da1mamba', icon: (
                          <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                          </svg>
                        )},
                      ]?.map((social) => (
                        <a
                          key={social?.href}
                          href={social?.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={social?.label}
                          style={{ color: 'var(--gray-dim)', transition: 'color 0.2s' }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--white)')}
                          onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--gray-dim)')}
                        >
                          {social?.icon}
                        </a>
                      ))}
                    </div>
                  </div>

                  {/* Google Reviews */}
                  <div style={{ marginTop: '20px' }}>
                    <a
                      href="https://share.google/GDSnfBC9lym6FSYWm"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-ghost"
                      style={{ fontSize: '12px', padding: '10px 18px', width: '100%', justifyContent: 'center' }}
                    >
                      View Google Reviews
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
